import React, {
  memo,
  useState,
  useCallback,
  useMemo,
  useEffect,
  cloneElement,
  forwardRef,
  ForwardedRef,
} from 'react';

import { FreeMode, Controller } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';

import 'swiper/css';
import 'swiper/css/free-mode';

import ItemsGrid from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsGrid';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import * as S from './style';

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
  pointColor?: string;
  emphasisTextColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

interface IProps {
  id?: string;
  items?: React.JSX.Element[];
  selectedValue?: number | string | undefined;
  speed?: number;
  colorType?: ColorType;
  isDesktop?: boolean;
  className?: string;
}

const Navigation = forwardRef(
  (
    {
      id,
      items,
      selectedValue,
      speed = 300,
      colorType = 'white',
      className,
      isDesktop,
    }: IProps,
    ref: ForwardedRef<HTMLDivElement>
  ): React.JSX.Element => {
    const [selectedValueState, setSelectedValueState] = useState<
      number | string | undefined
    >();
    const [centeredSlides, setCenteredSlides] = useState(false);
    const [currentSwiper, setCurrentSwiper] = useState<SwiperClass>();

    const onSwiperSlideTo = useCallback(
      (swiper: SwiperClass) => {
        if (items instanceof Array && selectedValue !== undefined) {
          const idx = items?.findIndex(
            (v: { props: { value: number | string | undefined } }) =>
              String(v.props.value) === String(selectedValue)
          );

          if (swiper && swiper.params)
            setCenteredSlides(
              idx === -1
                ? false
                : swiper.el.clientWidth < swiper.wrapperEl.scrollWidth
            );

          setTimeout(() => {
            if (swiper && idx !== -1 && swiper.params)
              swiper.slideTo(idx, speed, false);
          }, 100);
        }
      },
      [selectedValue, items, speed]
    );

    useEffect(() => {
      if (selectedValue !== selectedValueState) {
        setSelectedValueState(selectedValue);
        if (currentSwiper) onSwiperSlideTo(currentSwiper);
      }
    }, [selectedValue, currentSwiper, onSwiperSlideTo, selectedValueState]);

    const isCustomColor = typeof colorType === 'object';
    const customColors = isCustomColor ? colorType : undefined;

    const cName = useMemo(() => {
      const getNaviLineClass = () => {
        if (!items) return '';
        if (items.length > 10) return 'navi-3line';
        if (items.length > 5) return 'navi-2line';
        return 'navi-1line';
      };

      return classNameBind([
        'event-navigation',
        className || '',
        !isCustomColor && colorType ? `bg-type-${colorType}` : '',
        getNaviLineClass(),
      ]);
    }, [className, colorType, isCustomColor, items]);

    const resolvedItemColors =
      typeof colorType !== 'object'
        ? {
            bgColor: colorType,
            textColor: undefined,
            pointColor: undefined,
            emphasisTextColor: undefined,
          }
        : colorType;

    const getListRender = (el: React.ReactNode) => {
      if (el instanceof Array) {
        return el?.map((v: React.ReactElement<any>) => {
          if (v) {
            return (
              <SwiperSlide key={v.key} data-value={v.props.value ?? null}>
                {cloneElement(v, { selectedValue, ...resolvedItemColors })}
              </SwiperSlide>
            );
          }
          return null;
        });
      }
      return null;
    };

    return (
      <S.Navigation
        ref={ref}
        className={cName}
        id={id}
        $bgColor={customColors?.bgColor}
      >
        <div className="navi-inner">
          {isDesktop ? (
            <ItemsGrid perView={5} colGap={0} rowGap={0}>
              {items?.map((v, i) =>
                v
                  ? cloneElement(v, {
                      selectedValue,
                      ...resolvedItemColors,
                      key: v.key ?? i,
                    })
                  : null
              )}
            </ItemsGrid>
          ) : (
            <Swiper
              modules={[FreeMode, Controller]}
              spaceBetween={0}
              speed={speed}
              slidesPerView="auto"
              freeMode
              centeredSlides={centeredSlides}
              centeredSlidesBounds
              className="list"
              onInit={(swiper: SwiperClass) => {
                setCurrentSwiper(swiper);
                onSwiperSlideTo(swiper);
              }}
              onResize={(swiper: SwiperClass) => {
                if (selectedValue)
                  setCenteredSlides(
                    swiper.el.clientWidth < swiper.wrapperEl.scrollWidth
                  );
              }}
            >
              {getListRender(items)}
            </Swiper>
          )}
        </div>
      </S.Navigation>
    );
  }
);

Navigation.displayName = 'Navigation';

export default memo(Navigation);
